// components/ui/select.js
import React, { useEffect, useRef, useState } from 'react';

const Select = ({ children, defaultValue, value, onValueChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue || value || '');
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuWidthPx, setMenuWidthPx] = useState(undefined);
  const contentRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current) return;
      if (isOpen && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (isOpen && event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Keep menu width equal to trigger width to avoid alignment jumps
  useEffect(() => {
    if (triggerRef.current) {
      setMenuWidthPx(triggerRef.current.offsetWidth);
    }
  }, [isOpen]);
  
  return (
    <div className="aa-select-container" ref={containerRef}>
      {React.Children.map(children, child => {
        if (child.type === SelectTrigger) {
          return React.cloneElement(child, {
            onClick: () => setIsOpen(!isOpen),
            selectedValue,
            isOpen,
            ref: triggerRef
          });
        }
        if (child.type === SelectContent && isOpen) {
          return React.cloneElement(child, {
            onSelect: (val) => {
              setSelectedValue(val);
              setIsOpen(false);
              onValueChange?.(val);
            },
            width: menuWidthPx
          });
        }
        return null;
      })}
    </div>
  );
};

const SelectTrigger = React.forwardRef(({ children, className = '', onClick, selectedValue, isOpen, ...props }, ref) => {
  return (
    <button
      className={`aa-select-trigger ${isOpen ? 'aa-open' : ''} ${className}`}
      onClick={onClick}
      ref={ref}
      {...props}
    >
      {React.Children.map(children, child => {
        // Ensure the value displayed inside the trigger reflects the current selection
        if (child && child.type === SelectValue) {
          return React.cloneElement(child, { selectedValue });
        }
        return child;
      })}
      <svg className={``} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
});

const SelectValue = ({ placeholder, selectedValue }) => {
  return <span className={`aa-select-value ${selectedValue ? '' : 'placeholder'}`}>{selectedValue || placeholder}</span>;
};

const SelectContent = ({ children, onSelect, width }) => {
  return (
    <div className="aa-select-content" style={{ width }}>
      {React.Children.map(children, child => {
        if (child.type === SelectItem) {
          return React.cloneElement(child, { onSelect });
        }
        return child;
      })}
    </div>
  );
};

const SelectItem = ({ children, value, onSelect }) => {
  return (
    <div 
      className="aa-select-item"
      onClick={() => onSelect(value)}
    >
      {children}
    </div>
  );
};

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue };